import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { IImageLoadHandlerParams } from '@gem/components';
import { useTranslation } from '@gem/modules/i18n';

import Tasks from 'APP/Tasks';
import { ImageCropperPopup } from 'APP/main/PopupManager/Popups/ImageCropperPopup/ImageCropperPopup';
import { ICreateSpace, SpaceType } from 'APP/model/space/spaceModel.types';
import { useSpaceValidations } from 'APP/packages/validations/forms/forms';

export type ISpaceFormData = ICreateSpace;

export enum CreateSpaceStep {
  GeneralInfo = 'general-info',
  AdditionalInfo = 'additional-info',
}

interface ISpaceFormTypeOption {
  value: SpaceType;
  label: string;
  description: string;
}

interface ISpaceFormPresenterParams {
  initialValues: ISpaceFormData;
  isEditMode: boolean;
  newStep?: CreateSpaceStep;
  onStepChange?(step: CreateSpaceStep): void;
}

interface ISpaceFormPresenter {
  categories: {
    label: string;
    value: string;
  }[];
  step: CreateSpaceStep;
  initialValues: ISpaceFormData;
  isEditMode: boolean;
  nameValidate: any; // TODO: fix any after validation typings
  categoryValidate: any; // TODO: fix any after validation typings
  typeOptions: ISpaceFormTypeOption[];
  aliasValidate: any | null; // TODO: fix any after validation typings
  isGeneralInfo: boolean;
  isAdditionalInfo: boolean;
  isShowGeneralInfo: boolean;
  isShowAdditionalInfo: boolean;
  isShowPrevStep: boolean;
  isShowSubmit: boolean;
  setIsEditMode(mode: boolean): void;
  onShowAdditionalInfo(): void;
  loadCategories(): Promise<void>;
  forceUpdateForm(): void;
  onImageLoad(params: IImageLoadHandlerParams): void;
}

export function useSpaceFormPresenter({
  initialValues,
  isEditMode,
  newStep,
  onStepChange,
}: ISpaceFormPresenterParams): ISpaceFormPresenter {
  const { t } = useTranslation();
  const formValidations = useSpaceValidations();

  const presenter = useLocalObservable<ISpaceFormPresenter>(() => ({
    categories: [],
    step: CreateSpaceStep.GeneralInfo,
    initialValues,
    isEditMode: isEditMode,

    nameValidate: formValidations.spaceName.validate,
    categoryValidate: formValidations.spaceCategory.validate,

    typeOptions: [
      {
        value: SpaceType.Public,
        label: t('space_public_option'),
        description: t('space_public_option_subhint'),
      },
      {
        value: SpaceType.Private,
        label: t('space_private_option'),
        description: t('space_private_option_subhint'),
      },
    ],

    get aliasValidate(): any | null {
      if (presenter.isEditMode) {
        return formValidations.spaceAliasEdit.validate;
      }

      if (presenter.isAdditionalInfo) {
        return formValidations.spaceAliasCreate.validate;
      }

      return null;
    },

    get isGeneralInfo(): boolean {
      return presenter.step === CreateSpaceStep.GeneralInfo;
    },

    get isAdditionalInfo(): boolean {
      return presenter.step === CreateSpaceStep.AdditionalInfo;
    },

    get isShowGeneralInfo(): boolean {
      return presenter.isEditMode || presenter.isGeneralInfo;
    },

    get isShowAdditionalInfo(): boolean {
      return presenter.isEditMode || presenter.isAdditionalInfo;
    },

    get isShowPrevStep(): boolean {
      return !presenter.isEditMode && presenter.isAdditionalInfo;
    },

    get isShowSubmit(): boolean {
      return presenter.isEditMode || presenter.isAdditionalInfo;
    },

    setIsEditMode(mode: boolean): void {
      presenter.isEditMode = mode;
    },

    onShowAdditionalInfo(): void {
      presenter.step = CreateSpaceStep.AdditionalInfo;
      onStepChange?.(presenter.step);
    },

    async loadCategories(): Promise<void> {
      const categories = await Tasks.search.getAllCategoriesInfo();

      presenter.categories = categories.map((category) => ({
        label: category.displayName,
        value: category.id,
      }));

      presenter.forceUpdateForm();
    },

    /* force rerender fields, when data changes: when categories loaded */
    forceUpdateForm(): void {
      presenter.initialValues = { ...presenter.initialValues };
    },

    onImageLoad(params: IImageLoadHandlerParams): void {
      Tasks.app.openPopup(ImageCropperPopup, { params });
    },
  }));

  useEffect(() => {
    presenter.loadCategories();
  }, []);

  useEffect(() => {
    presenter.initialValues = initialValues;
  }, [initialValues]);

  useEffect(() => {
    if (newStep) {
      presenter.step = newStep;
    }
  }, [newStep]);

  useEffect(() => {
    presenter.setIsEditMode(isEditMode);
  }, [isEditMode]);

  return presenter;
}
